import { AnyObject } from 'shared/@types';
import { serverRequest } from 'shared/library/api-client';
import { platformOS } from 'shared/library/platform';
import { loadStoredAttribution, saveStoredAttribution } from './storage';
import type { AttributionPayload, AttributionTouch, StoredAttribution } from './types';

const lookbackDays = 90;
const sessionMinutes = 30;
const clickIdKeys = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid'] as const;
const campaignKeys = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  ...clickIdKeys,
];
let hasCapturedPage = false;
let lastCampaignKey: string | null = null;
let captureQueue = Promise.resolve();
const route = { method: 'post' as const, url: '/attribution' };
const ownedRootHosts = [
  'getapet.com',
  'getapet.se',
  'getapet.no',
  'getapet.dk',
  'localhost',
  '127.0.0.1',
];
const ignoredReferrerRootHosts = ['bankid.com', 'stripe.com', 'zignsec.com'];
type AttributionCaptureOptions = { isNewArrival?: boolean };
const searchEngineRootHosts = [
  ['baidu.com', 'baidu'],
  ['bing.com', 'bing'],
  ['brave.com', 'brave'],
  ['duckduckgo.com', 'duckduckgo'],
  ['ecosia.org', 'ecosia'],
  ['google.com', 'google'],
  ['kagi.com', 'kagi'],
  ['yahoo.com', 'yahoo'],
  ['yandex.com', 'yandex'],
  ['yandex.ru', 'yandex'],
] as const;
const socialRootHosts = [
  ['facebook.com', 'meta'],
  ['instagram.com', 'meta'],
  ['linkedin.com', 'linkedin'],
  ['pinterest.com', 'pinterest'],
  ['reddit.com', 'reddit'],
  ['threads.net', 'meta'],
  ['tiktok.com', 'tiktok'],
  ['twitter.com', 'x'],
  ['x.com', 'x'],
] as const;

function randomId(prefix: string) {
  const uuid =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;

  return `${prefix}_${uuid}`;
}

function nowIso() {
  return new Date().toISOString();
}

function parseUrl(url?: string | null): URL | null {
  if (!url) return null;

  try {
    return new URL(url);
  } catch {
    return null;
  }
}

function getCurrentUrl(): string | null {
  if (typeof window === 'undefined') return null;

  return window.location?.href ?? null;
}

function getReferrer(): string | null {
  if (typeof document === 'undefined') return null;

  return document.referrer || null;
}

function param(params: URLSearchParams, key: string): string | null {
  const value = params.get(key);

  return value && value.trim() !== '' ? value.trim() : null;
}

function normalizedSource(source?: string | null): string | null {
  if (!source) return null;

  const value = source.toLowerCase().replace(/[\s-]+/g, '_');

  if (['fb', 'facebook', 'instagram', 'ig', 'meta'].includes(value)) return 'meta';
  if (['googleads', 'google_ads', 'adwords'].includes(value)) return 'google';

  return value;
}

function normalizedMedium(medium?: string | null): string | null {
  if (!medium) return null;

  const value = medium.toLowerCase().replace(/[\s-]+/g, '_');

  if (['paid', 'paidsearch', 'paid_search', 'cpc', 'ppc'].includes(value)) return 'cpc';
  if (['paidsocial', 'paid_social'].includes(value)) return 'paid_social';
  if (['socialmedia', 'social_media'].includes(value)) return 'social';

  return value;
}

function hasClickId(touch: Partial<AttributionTouch>) {
  return clickIdKeys.some((key) => !!touch[key]);
}

function normalizeHost(host?: string | null): string | null {
  return host?.toLowerCase().replace(/^www\./, '') ?? null;
}

function matchesRootHost(host: string, rootHost: string) {
  return host === rootHost || host.endsWith(`.${rootHost}`);
}

function matchesAnyRootHost(host: string, rootHosts: readonly string[]) {
  return rootHosts.some((rootHost) => matchesRootHost(host, rootHost));
}

function isIgnoredReferrerHost(host: string) {
  return (
    matchesAnyRootHost(host, ownedRootHosts) || matchesAnyRootHost(host, ignoredReferrerRootHosts)
  );
}

function searchEngineSource(host: string): string | null {
  if (host.startsWith('google.') || host.includes('.google.')) {
    return 'google';
  }

  return searchEngineRootHosts.find(([rootHost]) => matchesRootHost(host, rootHost))?.[1] ?? null;
}

function socialSource(host: string): string | null {
  return socialRootHosts.find(([rootHost]) => matchesRootHost(host, rootHost))?.[1] ?? null;
}

function resolveChannel(
  source: string | null,
  medium: string | null,
  touch: Partial<AttributionTouch>,
) {
  if (medium === 'cpc' || medium === 'ppc') return 'paid_search';
  if (medium === 'paid_social') return 'paid_social';
  if (source && ['meta', 'tiktok'].includes(source))
    return hasClickId(touch) ? 'paid_social' : 'organic_social';
  if (medium === 'sms' || source === 'sms') return 'sms';
  if (medium === 'email' || source === 'email') return 'email';
  if (medium === 'organic') return 'organic_search';
  if (medium === 'social') return 'organic_social';
  if (medium === 'referral') return 'referral';
  if (source === 'direct' || medium === 'direct') return 'direct';

  return source || medium ? 'other' : 'unknown';
}

function referrerAttribution(
  referrer: string | null,
  current: URL | null,
): { source: string; medium: string } | null {
  const referrerUrl = parseUrl(referrer);

  if (!referrerUrl) return null;

  const referrerHost = normalizeHost(referrerUrl.hostname);
  const currentHost = normalizeHost(current?.hostname);

  if (!referrerHost || (currentHost && referrerHost === currentHost)) return null;
  if (isIgnoredReferrerHost(referrerHost)) return { source: 'direct', medium: 'direct' };

  const searchSource = searchEngineSource(referrerHost);
  if (searchSource) return { source: searchSource, medium: 'organic' };

  const social = socialSource(referrerHost);
  if (social) return { source: social, medium: 'social' };

  return { source: referrerHost, medium: 'referral' };
}

function createTouch(
  url?: string | null,
  referrer: string | null = null,
  includeCampaign = true,
): AttributionTouch {
  const currentUrl = url ?? getCurrentUrl();
  const current = parseUrl(currentUrl);
  const params = (includeCampaign && current?.searchParams) || new URLSearchParams();
  const clickIds = Object.fromEntries(clickIdKeys.map((key) => [key, param(params, key)]));
  let source = normalizedSource(param(params, 'utm_source'));
  let medium = normalizedMedium(param(params, 'utm_medium'));

  if (!source && (clickIds.gclid || clickIds.gbraid || clickIds.wbraid)) {
    source = 'google';
    medium = medium ?? 'cpc';
  }

  if (!source && clickIds.fbclid) {
    source = 'meta';
    medium = medium ?? 'paid_social';
  }

  if (!source && clickIds.msclkid) {
    source = 'microsoft';
    medium = medium ?? 'cpc';
  }

  if (!source && clickIds.ttclid) {
    source = 'tiktok';
    medium = medium ?? 'paid_social';
  }

  const referral = referrerAttribution(referrer, current);

  if (!source && referral) {
    source = referral.source;
    medium = medium ?? referral.medium;
  }

  if (!source && !medium) {
    source = 'direct';
    medium = 'direct';
  }

  const touch: AttributionTouch = {
    source,
    medium,
    campaign: param(params, 'utm_campaign'),
    term: param(params, 'utm_term'),
    content: param(params, 'utm_content'),
    platform: platformOS,
    landing_page: currentUrl ?? null,
    current_page: currentUrl ?? null,
    referrer,
    ...clickIds,
    occurred_at: nowIso(),
  };

  return {
    ...touch,
    channel: resolveChannel(source, medium, touch),
  };
}

function isDirect(touch?: AttributionTouch | null) {
  return (
    !touch || touch.channel === 'direct' || (touch.source === 'direct' && touch.medium === 'direct')
  );
}

function isExpired(touch?: AttributionTouch | null) {
  if (!touch) return true;

  const occurredAt = Date.parse(touch.occurred_at);

  return Number.isNaN(occurredAt) || Date.now() - occurredAt > lookbackDays * 24 * 60 * 60 * 1000;
}

function isSessionExpired(lastActivityAt?: string | null) {
  if (!lastActivityAt) return true;

  const startedAt = Date.parse(lastActivityAt);

  return Number.isNaN(startedAt) || Date.now() - startedAt > sessionMinutes * 60 * 1000;
}

async function loadOrCreateState(): Promise<StoredAttribution> {
  const stored = await loadStoredAttribution().catch(() => null);
  const expired = isSessionExpired(stored?.updated_at ?? stored?.session_started_at);

  return {
    anonymous_id: stored?.anonymous_id ?? randomId('anon'),
    session_id: stored && !expired ? stored.session_id : randomId('sess'),
    session_started_at: stored && !expired ? stored.session_started_at : nowIso(),
    first_touch: stored?.first_touch ?? null,
    last_touch: stored?.last_touch ?? null,
    last_non_direct_touch: !isExpired(stored?.last_non_direct_touch)
      ? stored?.last_non_direct_touch
      : null,
    updated_at: stored?.updated_at ?? nowIso(),
  };
}

async function capturePageAttribution(
  currentUrl: string | null,
  options: AttributionCaptureOptions,
) {
  const state = await loadOrCreateState();
  const current = parseUrl(currentUrl);
  const campaignKey = campaignKeys
    .map((key) => [key, current ? param(current.searchParams, key) : null])
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
    .join('&');
  const navigation =
    typeof window !== 'undefined'
      ? (window.performance?.getEntriesByType?.('navigation')[0] as
          | PerformanceNavigationTiming
          | undefined)
      : undefined;
  const restoredDocument =
    !hasCapturedPage && ['reload', 'back_forward'].includes(navigation?.type ?? '');
  // document.referrer belongs to the document arrival, not each SPA route or API request.
  const referrer = !hasCapturedPage && !restoredDocument ? getReferrer() : null;
  const referral = referrerAttribution(referrer, current);
  // Native Linking events are real arrivals even when the campaign URL matches a previous one.
  const newCampaign =
    !!campaignKey &&
    (options.isNewArrival || (campaignKey !== lastCampaignKey && !restoredDocument));
  const externalArrival = !!referral && referral.source !== 'direct';
  const directArrival =
    !hasCapturedPage && !restoredDocument && typeof document !== 'undefined' && !referrer;
  const expired = isSessionExpired(state.updated_at);
  const newTouch = !state.last_touch || newCampaign || externalArrival || directArrival || expired;
  const touch = newTouch
    ? createTouch(currentUrl, referrer, newCampaign || !state.last_touch)
    : { ...state.last_touch!, current_page: currentUrl ?? state.last_touch!.current_page };
  hasCapturedPage = true;
  if (campaignKey) lastCampaignKey = campaignKey;
  const next: StoredAttribution = {
    ...state,
    first_touch: state.first_touch ?? touch,
    last_touch: touch,
    last_non_direct_touch:
      newTouch && !isDirect(touch)
        ? touch
        : state.last_non_direct_touch
          ? {
              ...state.last_non_direct_touch,
              current_page: currentUrl ?? state.last_non_direct_touch.current_page,
            }
          : null,
    updated_at: nowIso(),
  };

  await saveStoredAttribution(next).catch(() => {});

  return next;
}

export function captureAttribution(
  url?: string | null,
  options: AttributionCaptureOptions = {},
): Promise<StoredAttribution> {
  const currentUrl = url ?? getCurrentUrl();
  // A route event and a conversion can arrive together; keep their storage writes ordered.
  const capture = captureQueue.then(() => capturePageAttribution(currentUrl, options));
  captureQueue = capture.then(
    () => {},
    () => {},
  );
  return capture;
}

export async function getAttributionPayload(eventType?: string): Promise<AttributionPayload> {
  const state = await loadOrCreateState();

  return {
    ...state,
    event_type: eventType,
  };
}

export async function withAttribution<T extends AnyObject>(
  data: T,
  eventType?: string,
): Promise<T & { attribution: AttributionPayload }> {
  const state = await captureAttribution();

  return {
    ...data,
    attribution: {
      ...state,
      event_type: eventType,
    },
  };
}

export async function recordAttribution(
  url?: string | null,
  options: AttributionCaptureOptions = {},
) {
  const state = await captureAttribution(url, options);
  const payload = {
    ...state,
    event_type: 'page_view',
  };

  if (isDirect(payload.last_touch)) {
    return;
  }

  await serverRequest(route, {
    attribution: payload,
  }).catch(() => {});
}

export type { AttributionPayload, AttributionTouch, StoredAttribution };
